import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from 'emailjs-com';

const roles = [
  {
    title: 'Rebar Estimation Specialist',
    type: 'Full-time',
    location: 'WFH',
    summary: 'Support high-accuracy quantity take-offs for commercial and infrastructure projects.',
  },
  {
    title: 'Detailing Engineer',
    type: 'Full-time',
    location: 'WFH',
    summary: 'Create clear, consistent rebar detailing deliverables for project teams and clients.',
  },
  {
    title: 'Project Coordinator',
    type: 'Part-time',
    location: 'WFH',
    summary: 'Coordinate schedules, document flow, and client communication across active assignments.',
  },
];

const emptyForm = {
  name: '',
  age: '',
  email: '',
  phone: '',
  location: '',
  experience: '',
  qualification: '',
  position: '',
  portfolio: '',
  message: '',
};

export default function CareersPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openForm = (position = '') => {
    setForm((currentForm) => ({ ...currentForm, position }));
    setErrors({});
    setStatus('');
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (!isSubmitting) setIsFormOpen(false);
  };

  const handleChange = ({ target }) => {
    setForm((currentForm) => ({ ...currentForm, [target.name]: target.value }));
    setErrors((currentErrors) => ({ ...currentErrors, [target.name]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Please enter your full name.';
    if (!form.age || Number(form.age) < 16 || Number(form.age) > 100) nextErrors.age = 'Enter a valid age.';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = 'Enter a valid email address.';
    if (!form.phone.trim()) nextErrors.phone = 'Please enter your phone number.';
    if (!form.location.trim()) nextErrors.location = 'Please enter your current location.';
    if (form.experience === '') nextErrors.experience = 'Please select your experience.';
    if (!form.position) nextErrors.position = 'Please select a position.';
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    setStatus('');
    try {
      await emailjs.send(
        'service_pbgoiql',
        'template_z395lk6',
        {
          ...form,
          to_email: 'Estimaterebbar@outlook.com',
          subject: `Career application: ${form.position} - ${form.name}`,
          message: [
            `Position: ${form.position}`,
            `Age: ${form.age}`,
            `Experience: ${form.experience} years`,
            `Location: ${form.location}`,
            `Qualification: ${form.qualification || 'Not provided'}`,
            `Portfolio / LinkedIn: ${form.portfolio || 'Not provided'}`,
            '',
            form.message || 'No additional message provided.',
          ].join('\n'),
        },
        'm2R65_Wa7RGzc2dvb'
      );
      setForm(emptyForm);
      setStatus('success');
    } catch (error) {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <section className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
            <div>
              <span className="inline-flex rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-white">Careers at EDRS</span>
              <h1 className="mt-6 text-4xl font-bold leading-tight sm:text-5xl">Build your future with a team that values precision and growth.</h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">We are looking for detail-oriented professionals who enjoy solving technical challenges and delivering dependable results for construction projects.</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <button type="button" onClick={() => openForm()} className="rounded-lg bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-300">Apply Now</button>
                <Link to="/contact" className="rounded-lg border border-white px-6 py-3 font-semibold text-white transition hover:bg-white hover:text-slate-900">Contact Us</Link>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6 shadow-2xl sm:p-8">
              <h2 className="text-2xl font-semibold text-white">Why join us?</h2>
              <ul className="mt-6 space-y-4 text-slate-300">
                <li>• Collaborate with experienced professionals in a fast-paced industry.</li>
                <li>• Work on meaningful projects with clear quality standards.</li>
                <li>• Enjoy flexibility, continuous learning, and room to grow.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="mb-8"><h2 className="text-3xl font-bold text-slate-900">Open Positions</h2><p className="mt-3 max-w-2xl text-slate-600">Explore current opportunities and find a role that matches your skills and ambition.</p></div>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {roles.map((role) => (
            <article key={role.title} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-500">{role.type}</p>
              <h3 className="mt-3 text-xl font-semibold text-slate-900">{role.title}</h3>
              <p className="mt-3 text-sm text-slate-600">{role.summary}</p>
              <p className="mt-4 text-sm font-medium text-slate-700">Location: {role.location}</p>
              <button type="button" onClick={() => openForm(role.title)} className="mt-6 self-start text-sm font-semibold text-sky-700 transition hover:text-sky-900 focus:outline-none focus:underline">Express Interest →</button>
            </article>
          ))}
        </div>
      </section>

      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-3 py-6 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="application-form-title" onMouseDown={(event) => { if (event.target === event.currentTarget) closeForm(); }}>
          <div className="w-full max-w-3xl rounded-2xl bg-white p-5 shadow-2xl sm:p-8">
            <div className="flex items-start justify-between gap-4"><div><h2 id="application-form-title" className="text-2xl font-bold text-slate-900 sm:text-3xl">Career application</h2><p className="mt-2 text-sm text-slate-600">Complete the details below. Your application will be sent to Estimaterebbar@outlook.com.</p></div><button type="button" onClick={closeForm} disabled={isSubmitting} aria-label="Close application form" className="rounded-md px-2 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50">×</button></div>
            {status === 'success' && <div className="mt-6 rounded-lg bg-green-50 p-4 text-green-800" role="status">Thank you. Your application has been sent successfully.</div>}
            {status === 'error' && <div className="mt-6 rounded-lg bg-red-50 p-4 text-red-800" role="alert">We could not send your application. Please try again or email Estimaterebbar@outlook.com directly.</div>}
            {status !== 'success' && <form className="mt-6 grid gap-5 sm:grid-cols-2" onSubmit={handleSubmit} noValidate>
              <label className="text-sm font-medium text-slate-700">Full name<input name="name" value={form.name} onChange={handleChange} autoComplete="name" className={inputClass} />{errors.name && <span className="mt-1 block text-xs text-red-600">{errors.name}</span>}</label>
              <label className="text-sm font-medium text-slate-700">Age<input type="number" name="age" value={form.age} onChange={handleChange} min="16" max="100" inputMode="numeric" className={inputClass} />{errors.age && <span className="mt-1 block text-xs text-red-600">{errors.age}</span>}</label>
              <label className="text-sm font-medium text-slate-700">Email address<input type="email" name="email" value={form.email} onChange={handleChange} autoComplete="email" className={inputClass} />{errors.email && <span className="mt-1 block text-xs text-red-600">{errors.email}</span>}</label>
              <label className="text-sm font-medium text-slate-700">Phone number<input type="tel" name="phone" value={form.phone} onChange={handleChange} autoComplete="tel" className={inputClass} />{errors.phone && <span className="mt-1 block text-xs text-red-600">{errors.phone}</span>}</label>
              <label className="text-sm font-medium text-slate-700">Current location<input name="location" value={form.location} onChange={handleChange} autoComplete="address-level2" className={inputClass} />{errors.location && <span className="mt-1 block text-xs text-red-600">{errors.location}</span>}</label>
              <label className="text-sm font-medium text-slate-700">Experience<select name="experience" value={form.experience} onChange={handleChange} className={inputClass}><option value="">Select experience</option><option value="0">Fresher</option><option value="1-2">1–2 years</option><option value="3-5">3–5 years</option><option value="6-10">6–10 years</option><option value="10+">10+ years</option></select>{errors.experience && <span className="mt-1 block text-xs text-red-600">{errors.experience}</span>}</label>
              <label className="text-sm font-medium text-slate-700">Position<select name="position" value={form.position} onChange={handleChange} className={inputClass}><option value="">Select a position</option>{roles.map((role) => <option key={role.title} value={role.title}>{role.title}</option>)}</select>{errors.position && <span className="mt-1 block text-xs text-red-600">{errors.position}</span>}</label>
              <label className="text-sm font-medium text-slate-700">Highest qualification<input name="qualification" value={form.qualification} onChange={handleChange} className={inputClass} /></label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-2">Portfolio or LinkedIn URL <span className="font-normal text-slate-500">(optional)</span><input type="url" name="portfolio" value={form.portfolio} onChange={handleChange} placeholder="https://" className={inputClass} /></label>
              <label className="text-sm font-medium text-slate-700 sm:col-span-2">Additional message <span className="font-normal text-slate-500">(optional)</span><textarea name="message" value={form.message} onChange={handleChange} rows="4" className={inputClass} /></label>
              <div className="flex flex-col-reverse gap-3 sm:col-span-2 sm:flex-row sm:justify-end"><button type="button" onClick={closeForm} disabled={isSubmitting} className="rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button><button type="submit" disabled={isSubmitting} className="rounded-lg bg-orange-500 px-5 py-3 font-semibold text-white hover:bg-orange-600 disabled:cursor-wait disabled:opacity-70">{isSubmitting ? 'Sending application…' : 'Submit application'}</button></div>
            </form>}
          </div>
        </div>
      )}
    </div>
  );
}
